import {
  Users,
  Package,
  DollarSign,
  Boxes,
  ShoppingCart,
  Store,
  LineChart,
  List,
  UserCircle,
  FilePlus2,
  Activity,
  CreditCard,
  LayoutDashboard,
  Crown,
  ChartColumnIncreasing,
  ClipboardList,
  FolderOpen,
  Folder,
  Table,
  Image,
  Heart,
  Search,
  MessageCircle,
  Mail,
  LayoutGrid,
} from "lucide-react";
import { LucideIcon } from "lucide-react";
import {
  SHOW_BUYER_ANALYTICS,
  SHOW_RFQ_FEATURES,
  SHOW_SELLER_ANALYTICS,
} from "./featureFlags";

export interface NavItem {
  name: string;
  icon: LucideIcon;
  href: string;
  /** Admin staff module key. Omitted items (e.g. Profile) are always visible. */
  module?: string;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

function filterRfqSections(sections: NavSection[]): NavSection[] {
  if (SHOW_RFQ_FEATURES) return sections;
  return sections.filter((s) => s.title !== "RFQ Management");
}

function filterBuyerAnalytics(sections: NavSection[]): NavSection[] {
  if (SHOW_BUYER_ANALYTICS) return sections;
  return sections.map((section) => ({
    ...section,
    title:
      section.title === "Dashboard & Analytics" ? "Dashboard" : section.title,
    items: section.items.filter((item) => item.href !== "/buyer/analytics"),
  }));
}

function filterSellerAnalytics(sections: NavSection[]): NavSection[] {
  if (SHOW_SELLER_ANALYTICS) return sections;
  return sections.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.href !== "/seller/analytics"),
  }));
}

// 👇 updated: both has buyer + seller sections grouped
const rawNavigationConfig: Record<
  "admin" | "seller" | "buyer" | "both",
  NavSection[] | { buyer: NavSection[]; seller: NavSection[] }
> = {
  admin: [
    {
      title: "Overview",
      items: [
        { name: "Dashboard", icon: ShoppingCart, href: "/admin/dashboard", module: "dashboard" },
        {
          name: "Reports & Analytics",
          icon: LineChart,
          href: "/admin/admin_reports",
          module: "reports",
        },
        { name: "Enquiries", icon: Mail, href: "/admin/enquiries", module: "enquiries" },
      ],
    },
    {
      title: "RFQ Management",
      items: [
        {
          name: "RFQ Management",
          icon: ClipboardList,
          href: "/admin/admin_rfq",
          module: "rfq",
        },
        {
          name: "RFQ Reports",
          icon: ChartColumnIncreasing,
          href: "/admin/admin_rfq_reports",
          module: "rfq",
        },
      ],
    },
    {
      title: "Chat",
      items: [
        {
          name: "Chat Monitoring",
          icon: MessageCircle,
          href: "/admin/admin_chat",
          module: "chat",
        },
      ],
    },
    {
      title: "User Management",
      items: [
        {
          name: "User Dashboard",
          icon: LayoutDashboard,
          href: "/admin/users/dashboard",
          module: "users",
        },
        { name: "User Search", icon: Search, href: "/admin/users/search", module: "users" },
        { name: "User List", icon: List, href: "/admin/users/list", module: "users" },
        // {
        //   name: "Sub-Account Management",
        //   icon: UserCircle,
        //   href: "/admin/sub_account_management",
        // },
      ],
    },
    {
      title: "Platform Management",
      items: [
        {
          name: "Product Dashboard",
          icon: LayoutDashboard,
          href: "/admin/products/dashboard",
          module: "products",
        },
        {
          name: "Product List",
          icon: List,
          href: "/admin/products/list",
          module: "products",
        },
        {
          name: "Product Search",
          icon: Search,
          href: "/admin/products/search",
          module: "products",
        },
        {
          name: "Create Product",
          icon: FilePlus2,
          href: "/admin/create_product",
          module: "products",
        },
        {
          name: "Categories",
          icon: Folder,
          href: "/admin/admin_categories",
          module: "categories",
        },
        {
          name: "Media Library",
          icon: Image,
          href: "/admin/media_management",
          module: "media",
        },
        {
          name: "Homepage",
          icon: LayoutGrid,
          href: "/admin/homepage",
          module: "homepage",
        },
      ],
    },
    {
      title: "Membership Management",
      items: [
        {
          name: "Manage Membership Plans",
          icon: Crown,
          href: "/admin/admin_membership_plans",
          module: "membership",
        },
        {
          name: "Membership Plans",
          icon: CreditCard,
          href: "/admin/membership_plans",
          module: "membership",
        },
      ],
    },
    {
      title: "Content & Settings",
      items: [
        { name: "Profile", icon: UserCircle, href: "/admin/profile_settings" },
      ],
    },
  ],

  seller: [
    {
      title: "Overview",
      items: [
        { name: "Dashboard", icon: ShoppingCart, href: "/seller/dashboard" },
        { name: "Analytics", icon: LineChart, href: "/seller/analytics" },
        { name: "Chat", icon: MessageCircle, href: "/chat" },
      ],
    },
    {
      title: "RFQ Management",
      items: [
        { name: "RFQ Inbox", icon: ClipboardList, href: "/seller/rfq-inbox" },
        { name: "My Quotes", icon: FilePlus2, href: "/seller/quotes" },
      ],
    },
    {
      title: "My Business",
      items: [
        {
          name: "Enquiries",
          icon: Mail,
          href: "/seller/enquiries",
        },
        {
          name: "Create Product",
          icon: FilePlus2,
          href: "/seller/create_product",
        },
        {
          name: "Storefront",
          icon: Store,
          href: "/seller/store_front",
        },
        {
          name: "Product Dashboard",
          icon: LayoutDashboard,
          href: "/seller/products/dashboard",
        },
        {
          name: "Product List",
          icon: List,
          href: "/seller/products/list",
        },
        {
          name: "Product Search",
          icon: Search,
          href: "/seller/products/search",
        },
        {
          name: "Media Management",
          icon: Image,
          href: "/seller/media_management",
        },
      ],
    },
    {
      title: "Account",
      items: [
        {
          name: "Membership Plans",
          icon: CreditCard,
          href: "/seller/membership_plans",
        },
        {
          name: "Business Profile",
          icon: UserCircle,
          href: "/seller/profile_settings",
        },
      ],
    },
  ],

  buyer: [
    {
      title: "Dashboard & Analytics",
      items: [
        {
          name: "Buyer Dashboard",
          icon: LayoutDashboard,
          href: "/buyer/dashboard",
        },
        { name: "Buyer Analytics", icon: Activity, href: "/buyer/analytics" },
        { name: "Chat", icon: MessageCircle, href: "/chat" },
      ],
    },
    {
      title: "RFQ Management",
      items: [
        {
          name: "RFQ List",
          icon: ClipboardList,
          href: "/buyer/rfq_list",
        },
        {
          name: "Create RFQ",
          icon: FilePlus2,
          href: "/buyer/rfq_create_form",
        },
        {
          name: "RFQ Dashboard",
          icon: FolderOpen,
          href: "/buyer/rfq_dashboard",
        },
        {
          name: "RFQ Quotes",
          icon: Table,
          href: "/buyer/rfq_quoteTable",
        },
      ],
    },
    {
      title: "Products",
      items: [
        {
          name: "Favorites",
          icon: Heart,
          href: "/buyer/favorites",
        },
        {
          name: "My Enquiries",
          icon: Mail,
          href: "/buyer/enquiries",
        },
      ],
    },
    {
      title: "Account",
      items: [
        { name: "Profile", icon: UserCircle, href: "/buyer/profile_settings" },
      ],
    },
  ],

  both: {
    buyer: [
      {
        title: "Dashboard & Analytics",
        items: [
          {
            name: "Buyer Dashboard",
            icon: LayoutDashboard,
            href: "/buyer/dashboard",
          },
          { name: "Buyer Analytics", icon: Activity, href: "/buyer/analytics" },
        ],
      },
      {
        title: "Products",
        items: [
          {
            name: "Favorites",
            icon: Heart,
            href: "/buyer/favorites",
          },
          {
            name: "My Enquiries",
            icon: Mail,
            href: "/buyer/enquiries",
          },
        ],
      },
      {
        title: "Account",
        items: [
          {
            name: "Profile",
            icon: UserCircle,
            href: "/buyer/profile_settings",
          },
        ],
      },
    ],

    seller: [
      {
        title: "My Business",
        items: [
          {
            name: "Create Product",
            icon: FilePlus2,
            href: "/seller/create_product",
          },
          // Storefront - Hidden as of now
          // { name: "Storefront", icon: Store, href: "/seller/store_front" },
          {
            name: "Seller Detail",
            icon: Store,
            href: "/seller_detail/:sellerId",
          },
          {
            name: "Product Dashboard",
            icon: LayoutDashboard,
            href: "/seller/products/dashboard",
          },
          {
            name: "Product Search",
            icon: Search,
            href: "/seller/products/search",
          },
          {
            name: "Product List",
            icon: List,
            href: "/seller/products/list",
          },
          {
            name: "Media Management",
            icon: Image,
            href: "/seller/media_management",
          },
        ],
      },
      {
        title: "Sales",
        items: [
          { name: "Dashboard", icon: ShoppingCart, href: "/seller/dashboard" },
          { name: "Analytics", icon: LineChart, href: "/seller/analytics" },
        ],
      },
      {
        title: "Account",
        items: [
          {
            name: "Membership Plans",
            icon: CreditCard,
            href: "/seller/membership_plans",
          },
          {
            name: "Business Profile",
            icon: UserCircle,
            href: "/seller/profile_settings",
          },
        ],
      },
    ],
  },
};

export const navigationConfig: Record<
  "admin" | "seller" | "buyer" | "both",
  NavSection[] | { buyer: NavSection[]; seller: NavSection[] }
> = {
  admin: filterRfqSections(
    rawNavigationConfig.admin as NavSection[],
  ) as NavSection[],
  seller: filterSellerAnalytics(
    filterRfqSections(rawNavigationConfig.seller as NavSection[]),
  ) as NavSection[],
  buyer: filterBuyerAnalytics(
    filterRfqSections(rawNavigationConfig.buyer as NavSection[]),
  ) as NavSection[],
  both: {
    buyer: filterBuyerAnalytics(
      filterRfqSections(
        (rawNavigationConfig.both as { buyer: NavSection[] }).buyer,
      ),
    ) as NavSection[],
    seller: filterSellerAnalytics(
      filterRfqSections(
        (rawNavigationConfig.both as { seller: NavSection[] }).seller,
      ),
    ) as NavSection[],
  },
};

const sellerFullSections = filterSellerAnalytics(
  filterRfqSections(rawNavigationConfig.seller as NavSection[]),
) as NavSection[];

/**
 * Seller nav filtered by approval and 3-step onboarding.
 * - Step 1: only Dashboard + Business Profile.
 * - Step 2: Dashboard, Membership Plans, Business Profile (no Analytics, Chat, or Storefront).
 * - Step 3: add Storefront.
 * - Approved: full seller nav.
 */
export function getSellerNavigationSections(
  sellerApproved: boolean,
  profileCompleteWithoutSocial: boolean,
  hasMembershipPlanSelected = false,
): NavSection[] {
  if (sellerApproved) {
    return sellerFullSections;
  }
  if (!profileCompleteWithoutSocial) {
    return [
      {
        title: "Overview",
        items: [
          { name: "Dashboard", icon: ShoppingCart, href: "/seller/dashboard" },
        ],
      },
      {
        title: "Account",
        items: [
          {
            name: "Business Profile",
            icon: UserCircle,
            href: "/seller/profile_settings",
          },
        ],
      },
    ];
  }
  const overviewItems: NavItem[] = [
    { name: "Dashboard", icon: ShoppingCart, href: "/seller/dashboard" },
  ];
  const accountItems: NavItem[] = [
    {
      name: "Membership Plans",
      icon: CreditCard,
      href: "/seller/membership_plans",
    },
    {
      name: "Business Profile",
      icon: UserCircle,
      href: "/seller/profile_settings",
    },
  ];
  const sections: NavSection[] = [
    { title: "Overview", items: overviewItems },
    { title: "Account", items: accountItems },
  ];
  if (hasMembershipPlanSelected) {
    sections.splice(1, 0, {
      title: "My Business",
      items: [{ name: "Storefront", icon: Store, href: "/seller/store_front" }],
    });
  }
  return sections;
}

export const ADMIN_STAFF_MODULE_CATALOG: { key: string; label: string }[] = [
  { key: "dashboard", label: "Dashboard" },
  { key: "reports", label: "Reports & Analytics" },
  { key: "enquiries", label: "Enquiries" },
  { key: "rfq", label: "RFQ Management" },
  { key: "chat", label: "Chat Monitoring" },
  { key: "users", label: "User Management" },
  { key: "products", label: "Products" },
  { key: "categories", label: "Categories" },
  { key: "media", label: "Media Library" },
  { key: "homepage", label: "Homepage" },
  { key: "membership", label: "Membership Plans" },
];

const extraAdminPathModules: { prefix: string; module: string }[] = [
  { prefix: "/admin/create_product", module: "products" },
  { prefix: "/admin/users", module: "users" },
  { prefix: "/admin/products", module: "products" },
  { prefix: "/admin/userprofile", module: "users" },
];

export function getAdminNavigationSections(
  allowedModules?: string[],
): NavSection[] {
  const sections = navigationConfig.admin as NavSection[];
  if (allowedModules === undefined) {
    return sections;
  }
  const allowed = new Set(allowedModules);
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.module) {
          return true;
        }
        return allowed.has(item.module);
      }),
    }))
    .filter((section) => section.items.length > 0);
}

export function getAdminModuleForPath(
  pathname: string,
): string | "profile" | null {
  if (/^\/admin\/(profile_settings|profile)(\/|$)/.test(pathname)) {
    return "profile";
  }
  const sections = navigationConfig.admin as NavSection[];
  for (const section of sections) {
    for (const item of section.items) {
      if (pathname === item.href || pathname.startsWith(`${item.href}/`)) {
        return item.module || "profile";
      }
    }
  }
  for (const extra of extraAdminPathModules) {
    if (pathname === extra.prefix || pathname.startsWith(`${extra.prefix}/`)) {
      return extra.module;
    }
  }
  return null;
}

export function getFirstGrantedAdminPath(allowedModules: string[]): string {
  const sections = getAdminNavigationSections(allowedModules);
  for (const section of sections) {
    const granted = section.items.find((item) => item.module);
    if (granted) {
      return granted.href;
    }
  }
  return "/admin/profile_settings";
}

export function canAccessAdminPath(
  pathname: string,
  allowedModules?: string[],
): boolean {
  if (allowedModules === undefined) {
    return true;
  }
  const moduleKey = getAdminModuleForPath(pathname);
  if (moduleKey === "profile") {
    return true;
  }
  if (!moduleKey) {
    return false;
  }
  return allowedModules.includes(moduleKey);
}
